<template>
    <div class="d-flex flex-column position-relative">
        <template v-if="mode == 'icon'">
            <button-link
                :disabled="disabled"
                :icon="icon"
                :icon-size="iconSize"
                :loading="uploading"
                type="button"
                @click="triggerClick"
            />
        </template>

        <template v-else>
            <base-button
                :disabled="disabled"
                :loading="uploading"
                :style="`width: ${width}px`"
                icon="export"
                theme="secondary"
                type="button"
                @click="triggerClick"
            >
                {{ $t("common.uploadFile") }}
            </base-button>
        </template>

        <input
            ref="inputFile"
            type="file"
            :disabled="disabled || uploading"
            :accept="allowedExtensions"
            class="opacity--0 position-absolute left bottom pointer-events-none"
            @change="upload"
        />

        <input-status :status="status" />
    </div>
</template>

<script>
import { isNativelyPreviewable, readAsDataURL, ucfirst } from "~/lib/utils"
import { notify } from "~/lib/bugsnag"

import BaseButton from "~/components/buttons/BaseButton"
import ButtonLink from "~/components/buttons/ButtonLink"
import InputStatus from "~/components/inputs/InputStatus"

async function addPreviewUrlIfPossible({ attachment, file, maxFileSize }) {
    // Big files are not previewable using data url
    if (
        attachment.size < maxFileSize &&
        isNativelyPreviewable(attachment.extension)
    ) {
        try {
            attachment.previewUrl = await readAsDataURL(file)
        } catch {
            console.error("readAsDataURL failed to create the dataURL")
        }
    }
}

export default {
    inject: ["error"],

    props: {
        icon: {
            type: String,
            default: "paperclip",
        },

        iconSize: {
            type: Number,
            default: 24,
        },

        extensions: {
            type: Array,
            default: () => [],
        },

        disabled: Boolean,
        mode: {
            type: String,
            default: "icon",
            validator: val => ["icon", "button"].includes(val),
        },

        width: {
            type: Number,
        },

        ...InputStatus.props,
    },

    emits: ["start-upload", "new-file", "end-upload"],

    data() {
        return {
            uploading: false,
        }
    },

    computed: {
        maxFileSize() {
            return this.$store.state.configs.maxFileSizeUpload
        },

        allowedExtensions() {
            if (this.extensions.length)
                return this.extensions.map(e => `.${e}`).join(",")
            return this.$store.getters.allowedExtensions
                .map(e => `.${e}`)
                .join(",")
        },
    },

    methods: {
        triggerClick() {
            this.$refs.inputFile.click()
        },

        async upload(event) {
            const files = Array.from(event.target.files)
            if (files.length == 0) return

            if (files[0].size > this.maxFileSize) {
                this.error(ucfirst(this.$t("common.fileIsTooBig")))
                return
            }

            let attachment = null
            this.uploading = true
            this.$emit("start-upload")

            try {
                attachment = await this.$http.uploadFile(files[0])
                await addPreviewUrlIfPossible({
                    attachment,
                    file: files[0],
                    maxFileSize: this.maxFileSize / 10,
                })
                this.$emit("new-file", attachment)
            } catch (error) {
                this.error(ucfirst(error.message))
                notify(error)
            } finally {
                this.uploading = false
                this.$emit("end-upload")
                event.target.value = ""
            }
        },
    },

    components: {
        ButtonLink,
        BaseButton,
        InputStatus,
    },
}
</script>
